import { Inject, ModelEvents, Singleton } from '@denishussar/html5-game-engine';
import { getLevelConfig, getPaintConfig, getUpgradeConfig } from '../Common/GameConfig';
import { GarageEvents, UIEvents } from '../Constants/Events';
import { GameModel } from '../Models/GameModel';
import { Backend } from '../Backend';
import { GameLogic } from './GameLogic';

@Singleton
export class GarageLogic {
    constructor(
        @Inject private gameLogic: GameLogic,
        @Inject private gameModel: GameModel,
        @Inject private modelEvents: ModelEvents,
        @Inject private backend: Backend,
    ) {
    }

    selectCar(index: number) {
        this.backend.saveState({
            currentCar: index,
        })
            .then(() => {
                this.gameModel.garage_selectedCar = index;
                this.gameModel.currentPlayer = this.gameModel.cars[index];
                this.gameModel.currentPlayer.updateProperties();
            })
            .catch(() => {
                this.modelEvents.dispatch(this.gameModel, UIEvents.showError);
            });
    }

    selectUpgrade(index: number) {
        this.gameModel.garage_selectedUpgrade = index;
    }

    selectPaint(index: number) {
        this.gameModel.garage_selectedPaint = index;
        if (this.gameModel.currentPlayer.availablePaints[index]) {
            const { cars } = this.backend.getState();
            const currentCar = cars.find(car => car.index === this.gameModel.currentPlayer.index);

            currentCar.color = index;

            this.backend.saveState({
                cars: cars,
            })
                .then(() => {
                    this.gameModel.garage_selectedPaint = index;

                    if (this.gameModel.currentPlayer.availablePaints[index]) {
                        this.gameModel.currentPlayer.paint = index;
                        this.modelEvents.dispatch(this.gameModel, GarageEvents.buyPaint);
                    }

                })
                .catch(() => {
                    this.modelEvents.dispatch(this.gameModel, UIEvents.showError);
                });
        }
    }

    buyCar() {
        const price = this.gameModel.currentPlayer.price;

        if (!this.gameModel.currentPlayer.isBought) {
            if (price <= this.gameModel.coins) {
                const { coins, cars } = this.backend.getState();
                const currentCar = cars.find(car => car.index === this.gameModel.currentPlayer.index);

                currentCar.locked = false;

                this.backend.saveState({
                    cars: cars,
                    coins: coins - price,
                })
                    .then(() => {
                        this.gameModel.currentPlayer.isBought = true;
                        this.gameModel.coins -= this.gameModel.currentPlayer.price;
                        this.modelEvents.dispatch(this.gameModel, GarageEvents.buyCar, this.gameModel.garage_selectedCar);
                    })
                    .catch(() => {
                        this.modelEvents.dispatch(this.gameModel, UIEvents.showError);
                    });
            }
        }
    }

    buyUpgrade() {
        const player = this.gameModel.currentPlayer;
        const upgradeIndex = this.gameModel.garage_selectedUpgrade;
        const price = getUpgradeConfig(upgradeIndex, 'price');

        if (price <= this.gameModel.coins) {
            const { coins, cars } = this.backend.getState();

            this.backend.saveState({
                cars: cars,
                coins: coins - price,
            })
                .then(() => {
                    this.gameModel.coins -= price;

                    player.upgrades[upgradeIndex] = +player.upgrades[upgradeIndex] + 1;
                    player.updateProperties();

                    this.modelEvents.dispatch(this.gameModel, GarageEvents.buyUpgrade);
                })
                .catch(() => {
                    this.modelEvents.dispatch(this.gameModel, UIEvents.showError);
                });
        }
    }

    buyPaint() {
        const player = this.gameModel.currentPlayer;
        const index = this.gameModel.garage_selectedPaint;

        if (this.gameModel.currentPlayer.paint == this.gameModel.garage_selectedPaint) {
            return;
        }

        const { coins, cars } = this.backend.getState();
        const currentCar = cars.find(car => car.index === this.gameModel.currentPlayer.index);

        if (player.availablePaints[index]) {
            currentCar.color = index;

            this.backend.saveState({
                cars: cars,
            })
                .then(() => {
                    player.paint = index;
                    this.modelEvents.dispatch(this.gameModel, GarageEvents.buyPaint);
                })
                .catch(() => {
                    this.modelEvents.dispatch(this.gameModel, UIEvents.showError);
                });

        } else {
            const price = getPaintConfig(index, 'price');

            if (price <= this.gameModel.coins) {
                currentCar.color = index;
                currentCar.unlockedColors[index] = true;

                this.backend.saveState({
                    cars: cars,
                    coins: coins - price,
                })
                    .then(() => {
                        this.gameModel.coins -= price;
                        player.availablePaints[index] = true;
                        player.paint = index;
                        this.modelEvents.dispatch(this.gameModel, GarageEvents.buyPaint);
                    })
                    .catch(() => {
                        this.modelEvents.dispatch(this.gameModel, UIEvents.showError);
                    });
            }
        }
    }

    buyLevel(index) {
        const price = getLevelConfig(index, 'price');
        if (price <= this.gameModel.coins) {
            const { coins, unlockedLevels } = this.backend.getState();
            unlockedLevels[index] = true;
            this.backend.saveState({
                unlockedLevels: unlockedLevels,
                coins: coins - price,
            })
                .then(() => {
                    this.gameModel.coins -= price;
                    this.modelEvents.dispatch(this.gameModel, GarageEvents.buyLevel, index);
                })
                .catch(() => {
                    this.modelEvents.dispatch(this.gameModel, UIEvents.showError);
                });

        }
    }

    calcRepairPrice(): number {
        return (100 - this.gameModel.currentPlayer.health) * this.gameModel.currentPlayer.repairPrice;
    }

    repairCar(): boolean {
        const price = this.calcRepairPrice();
        let success = false;
        if (price <= this.gameModel.coins) {
            const { coins, cars } = this.backend.getState();
            const currentCar = cars.find(car => car.index === this.gameModel.currentPlayer.index);
            success = true;
            currentCar.health = 100;

            this.backend.saveState({
                cars: cars,
                coins: coins - price,
            })
                .then(() => {
                    this.gameModel.coins -= this.calcRepairPrice();
                    this.gameModel.currentPlayer.health = 100;
                    this.modelEvents.dispatch(this.gameModel, GarageEvents.repairCar, this.gameModel.garage_selectedCar);
                    success = true;
                })
                .catch(() => {
                    this.modelEvents.dispatch(this.gameModel, UIEvents.showError);
                });
            if (success) return true;
            return false;
        }
    }
}
